import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePreference, usePreferences } from '@/api/preferences';

/**
 * Applies the user preferences that act on the whole shell (general.md §4.13). Rendered once inside App.
 *
 *  - `display.density` → `<html data-density>` (the CSS is in index.css; index.html sets it on first paint).
 *  - `general.landingPage` → when a tab first opens Cosmos at `/` (sign-in lands there too), go to the chosen
 *    start page. Once per tab (sessionStorage), and only if you're still on `/`, so the Home link keeps working.
 */

const LANDED_KEY = 'cosmos:landed';

/** Called on the sign-in page, so the next sign-in honours the start page again. */
export function resetLanding() {
  try { sessionStorage.removeItem(LANDED_KEY); } catch { /* storage unavailable */ }
}

export function PreferenceEffects() {
  const density = usePreference('display.density');
  useEffect(() => {
    if (density === 'compact') document.documentElement.setAttribute('data-density', 'compact');
    else document.documentElement.removeAttribute('data-density');
  }, [density]);

  useLandingPage();
  return null;
}

function useLandingPage() {
  const { data, isError } = usePreferences();
  const loc = useLocation();
  const navigate = useNavigate();
  const decided = useRef(false);

  useEffect(() => {
    if (decided.current) return;
    let landed = false;
    try { landed = sessionStorage.getItem(LANDED_KEY) === '1'; } catch { /* storage unavailable */ }
    if (landed) { decided.current = true; return; }
    // Wait for the server's value: the localStorage mirror may be missing or stale in this browser.
    if (!data && !isError) return;
    decided.current = true;
    try { sessionStorage.setItem(LANDED_KEY, '1'); } catch { /* storage unavailable */ }
    const target = data?.values['general.landingPage'] ?? '/';
    if (loc.pathname === '/' && !loc.search && target !== '/') navigate(target, { replace: true });
  }, [data, isError, loc.pathname, loc.search, navigate]);
}
